/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { defineMessages } from 'react-intl';

export const Register = defineMessages({
  basicInfo: {
    id: 'CatalogOpenApi.Register.basicInfo',
    defaultMessage: 'Basic information'
  },
  basicInfoDescription: {
    id: 'CatalogOpenApi.Register.basicInfoDescription',
    defaultMessage:
      'Having a clear name and detailed description will help provide context and clarity to what your extension does.'
  },
  nonEmptyTitle: {
    id: 'CatalogOpenApi.Register.nonEmptyTitle',
    defaultMessage: 'Extension name cannot be empty'
  },
  nonEmptyDescription: {
    id: 'CatalogOpenApi.Register.nonEmptyDescription',
    defaultMessage: 'Extension description cannot be empty'
  },
  buttonAction: {
    id: 'CatalogOpenApi.Register.buttonAction',
    defaultMessage: 'Build custom extension'
  },
  customExtensionTitle: {
    id: 'CatalogOpenApi.Register.customExtensionTitle',
    defaultMessage: 'Custom extension'
  },
  extensionUploadErrorTitle: {
    id: 'CatalogOpenApi.Register.extensionUploadErrorTitle',
    defaultMessage: 'Import failed'
  },
  extensionUploadErrorSubtitle: {
    id: 'CatalogOpenApi.Register.extensionUploadErrorSubtitle',
    defaultMessage:
      'Please adjust your OpenAPI specification and try again. Reason: '
  },
  extensionDescriptionLabel: {
    id: 'CatalogOpenApi.Register.extensionDescriptionLabel',
    defaultMessage: 'Extension description'
  },
  extensionDescriptionMaxLength: {
    id: 'CatalogOpenApi.Register.extensionDescriptionMaxLength',
    defaultMessage: 'Extension description has a max length of 128 characters.' // TODO: 128 should be a variable I pass in I'm assuming...
  },
  extensionDescriptionPlaceholder: {
    id: 'CatalogOpenApi.Register.extensionDescriptionPlaceholder',
    defaultMessage:
      'Example: This extension provides an integration with an external application.'
  },
  extensionNameLabel: {
    id: 'CatalogOpenApi.Register.extensionNameLabel',
    defaultMessage: 'Extension name'
  },
  extensionNameMaxLength: {
    id: 'CatalogOpenApi.Register.extensionNameMaxLength',
    defaultMessage: 'Extension name has a max length of 64 characters.' // TODO: 64 should be a variable I pass in I'm assuming...
  },
  extensionDuplicateExists: {
    id: 'CatalogOpenApi.Register.extensionDuplicateExists',
    defaultMessage: 'Extension name already exists'
  },
  extensionNameRequired: {
    id: 'CatalogOpenApi.Register.extensionNameRequired',
    defaultMessage: 'Extension name is required'
  },
  extensionNamePlaceholder: {
    id: 'CatalogOpenApi.Register.extensionNamePlaceholder',
    defaultMessage: 'Example: My custom extension'
  },
  extensionDescriptionRequired: {
    id: 'CatalogOpenApi.Register.extensionDescriptionRequired',
    defaultMessage: 'Extension description is required'
  },
  importLabelDescription: {
    id: 'CatalogOpenApi.Register.importLabelDescription',
    defaultMessage:
      'Import an OpenAPI document in a .json format, describing the authentication and methods for your extension.'
  },
  uploadSuccessTitle: {
    id: 'CatalogOpenApi.Register.uploadSuccessTitle',
    defaultMessage: 'Update successful'
  },
  uploadSuccessSubtitle: {
    id: 'CatalogOpenApi.Register.uploadSuccessSubtitle',
    defaultMessage:
      'The extension OpenAPI document for this extension has been updated.'
  },
  updateImportLabelDescription: {
    id: 'CatalogOpenApi.Register.updateImportLabelDescription',
    defaultMessage:
      'Import the updated OpenAPI document for this extension in a .json format to permanently replace the document attached to this custom extension.'
  },
  updateNote: {
    id: 'CatalogOpenApi.Register.updateImportLabelDescription',
    defaultMessage:
      'Note that uploading a new OpenAPI document will change the spec hash ID used in action skills and will not update other environments until your action skill has been published to those environments.'
  },
  importLabelText: {
    id: 'CatalogOpenApi.Register.importLabelText',
    defaultMessage: 'Drag and drop file here or click to upload'
  },
  importOpenApi: {
    id: 'CatalogOpenApi.Register.importOpenApi',
    defaultMessage: 'Import OpenAPI'
  },
  importOpenApiDelete: {
    id: 'phone.PhoneImportModal.importOpenApiDelete',
    defaultMessage: 'Clear OpenAPI document as JSON File'
  },
  importOpenApiError: {
    id: 'phone.PhoneImportModal.importOpenApiError',
    defaultMessage: 'Error occurred importing OpenAPI document as JSON file:'
  },
  introDescription: {
    id: 'CatalogOpenApi.Register.introDescription',
    defaultMessage:
      'Create a custom extension to tailor the experience for your customers.'
  },
  introInstructions: {
    id: 'CatalogOpenApi.Register.introInstructions',
    defaultMessage: 'Steps to setting up custom extensions:'
  },
  introListItemOne: {
    id: 'CatalogOpenApi.Register.introListItemOne',
    defaultMessage:
      'Provide an extension name and description that clearly explains what the extension accomplishes.'
  },
  introListItemTwo: {
    id: 'CatalogOpenApi.Register.introListItemTwo',
    defaultMessage:
      'Import your OpenAPI document as a JSON file to start building your custom extension.'
  },
  introListItemThree: {
    id: 'CatalogOpenApi.Register.introListItemThree',
    defaultMessage:
      'Select the environment (Production, Development, or Stage) to use your extension and the authentication type to complete the setup process.'
  },
  reviewExtension: {
    id: 'CatalogOpenApi.Register.reviewExtension',
    defaultMessage: 'Review extension'
  },
  reviewExtensionDescription: {
    id: 'CatalogOpenApi.Register.reviewExtensionDescription',
    defaultMessage:
      'Review the servers and extension resources provided in the OpenAPI document.'
  },
  reviewAuthentication: {
    id: 'CatalogOpenApi.Register.reviewAuthentication',
    defaultMessage: 'Review authentication'
  },
  reviewAuthenticationDescription: {
    id: 'CatalogOpenApi.Register.reviewAuthenticationDescription',
    defaultMessage:
      'Provided is a list of the authentication methods found within the OpenAPI document.'
  },
  reviewAuthenticationColumn: {
    id: 'CatalogOpenApi.Register.reviewAuthenticationColumn',
    defaultMessage: 'Authentication type'
  },
  reviewAuthenticationColumnFields: {
    id: 'CatalogOpenApi.Register.reviewAuthenticationColumnFields',
    defaultMessage: 'Required fields'
  },
  reviewAuthenticationnBearerField: {
    id: 'CatalogOpenApi.Register.reviewAuthenticationnBearerField',
    defaultMessage: 'bearer token'
  },
  reviewAuthenticationOAuthPasswordField: {
    id: 'CatalogOpenApi.Register.reviewAuthenticationOAuthPasswordField',
    defaultMessage:
      'Password flow: client ID, client secret, username, password'
  },
  reviewAuthenticationOAuthClientCredentialsField: {
    id: 'CatalogOpenApi.Register.reviewAuthenticationOAuthClientCredentialsField',
    defaultMessage: 'Client credentials flow: client ID, client secret'
  },
  reviewAuthenticationOAuthAuthCodeField: {
    id: 'CatalogOpenApi.Register.reviewAuthenticationOAuthAuthCodeField',
    defaultMessage: 'Authorization code flow: client ID, client secret'
  },
  reviewAuthenticationOAuthCustomFlowField: {
    id: 'CatalogOpenApi.Register.reviewAuthenticationOAuthCustomFlowField',
    defaultMessage: 'Custom flow {flowName}: secret keys - {customFields}'
  },
  reviewAuthenticationBasicAuthField: {
    id: 'CatalogOpenApi.Register.reviewAuthenticationBasicAuthField',
    defaultMessage: 'username, password'
  },
  reviewAuthenticationApiKeyField: {
    id: 'CatalogOpenApi.Register.reviewAuthenticationApiKeyField',
    defaultMessage: '{name} in {in}'
  },
  reviewServers: {
    id: 'CatalogOpenApi.Register.reviewServers',
    defaultMessage: 'Review servers'
  },
  reviewServersDescription: {
    id: 'CatalogOpenApi.Register.reviewServersDescription',
    defaultMessage:
      'Provided is a list of the servers and server variables found within the OpenAPI document.'
  },
  reviewServersColumnUrl: {
    id: 'CatalogOpenApi.Register.reviewServersColumnUrl',
    defaultMessage: 'URL'
  },
  reviewServersColumnDescription: {
    id: 'CatalogOpenApi.Register.reviewServersColumnDescription',
    defaultMessage: 'Description'
  },
  reviewServersColumnVariables: {
    id: 'CatalogOpenApi.Register.reviewServersColumnVariables',
    defaultMessage: 'Variables'
  },
  reviewSchemas: {
    id: 'CatalogOpenApi.Register.reviewSchemas',
    defaultMessage: 'Review schemas'
  },
  reviewSchemasDescription: {
    id: 'CatalogOpenApi.Register.reviewSchemasDescription',
    defaultMessage:
      'Provided is a list of the schema variables found within the OpenAPI document.'
  },
  reviewSchemasColumnSchema: {
    id: 'CatalogOpenApi.Register.reviewSchemasColumnSchema',
    defaultMessage: 'Schema'
  },
  setUpTitle: {
    id: 'CatalogOpenApi.Register.setUpTitle',
    defaultMessage: 'Set up custom extension'
  },
  noOAuthFLowPresent: {
    id: 'CatalogOpenApi.Register.noOAuthFLowPresent',
    defaultMessage:
      'Your OpenAPI file needs supported flow types (password, client credentials or authorization code) for OAuth configuration!'
  }
});
